import CloseIcon from '@mui/icons-material/Close';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { Chip, Stack, Typography, useTheme } from '@mui/material';
import {
  ConversationChannelType,
  ConversationParticipantEntity,
} from '@provision/conversation-types';
import { Tooltip } from 'src/components';
import { SelectMenuTriggerProps } from 'src/components/SelectMenu';
import { ParticipantChipMenu } from './participant-chip-menu-items';
import { participantChipSx } from './participant-chip.styles';
import { ParticipantSelectorItem } from './types';

interface ParticipantChipItemProps {
  participant: ParticipantSelectorItem;
  isInvalid?: boolean;
  readOnly?: boolean;
  onDelete?: () => void;
  onAddEmail: (data: ParticipantSelectorItem, email: string) => void;
  onRemoveEmail: (data: ParticipantSelectorItem, email: string) => void;
}

export const ParticipantChipItem = ({
  participant,
  isInvalid = false,
  readOnly = false,
  onDelete,
  onAddEmail,
  onRemoveEmail,
}: ParticipantChipItemProps) => {
  const theme = useTheme();
  const hasEmailMenu =
    !readOnly && participant.participantEntity !== ConversationParticipantEntity.Unknown;

  const label = participant.fullName || participant.participantIdentity;

  const emails = participant.channels?.filter?.(
    (channel) =>
      participant.participantEntity !== ConversationParticipantEntity.Unknown &&
      (channel.conversationChannelTypeId as ConversationChannelType) ===
        ConversationChannelType.Email,
  );

  const emailsCount = emails?.length || 0;

  const tooltipTitle = (
    <Typography
      variant="subtitle2"
      sx={{ whiteSpace: 'preserve-breaks', fontSize: '0.75rem' }}
    >
      {[label, ...emails.map((it) => it.externalIdentifier)].join('\n')}
    </Typography>
  );

  const renderChip = (triggerProps?: SelectMenuTriggerProps) => (
    <Chip
      {...triggerProps}
      label={
        <Tooltip title={tooltipTitle} enterDelay={1000}>
          <Stack direction="row" gap="0.25rem" alignItems="center">
            <Typography
              variant="body2"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              overflow="hidden"
              sx={{
                fontSize: '0.75rem',
                lineHeight: '1rem',
                ...(isInvalid && { color: theme.palette.error.main }),
              }}
            >
              {label}
            </Typography>

            {emailsCount > 0 && (
              <Stack direction="row" gap="0.25rem" alignItems="center">
                <EmailOutlinedIcon
                  sx={{
                    fontSize: '1rem',
                    color: theme.palette.custom.OV.sys.secondary,
                  }}
                />
                {emailsCount > 1 && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.75rem',
                      lineHeight: '1rem',
                      marginRight: '0.25rem',
                      color: theme.palette.custom.OV.sys.secondary,
                    }}
                  >
                    {emailsCount}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        </Tooltip>
      }
      onDelete={readOnly ? undefined : onDelete}
      deleteIcon={
        onDelete && !readOnly ? (
          <Tooltip title="Remove" enterDelay={3000}>
            <CloseIcon />
          </Tooltip>
        ) : undefined
      }
      sx={{
        ...participantChipSx(theme),
        ...(isInvalid && {
          border: `1px solid ${theme.palette.error.main}`,
        }),
        ...(!readOnly && {
          '&:hover': {
            backgroundColor:
              participant.participantEntity !== ConversationParticipantEntity.Unknown
                ? theme.palette.custom.OV.sys.surfaceVariant
                : undefined,
          },
        }),
      }}
    />
  );

  if (!hasEmailMenu) {
    return renderChip();
  }

  return (
    <ParticipantChipMenu
      participant={participant}
      onAddEmail={onAddEmail}
      onRemoveEmail={onRemoveEmail}
      renderTrigger={({ getTriggerProps }) => renderChip(getTriggerProps())}
    />
  );
};
